import { action, autorun, computed, observable, reaction, toJS } from "mobx";
import { FormStore } from "@modules/formx";
import { merge } from "lodash";
import { professions, regexps } from "@bonliva-crm/common";
import { store } from "@store";
import locale from "@bonliva-crm/locale";
import { Navigation } from "@navigation";
import { Option } from "@components/Select";
import {
  CustomerAOESettingsInput,
  CustomerCardFragment,
  CustomerEditor_Create,
  CustomerEditor_CustomerFragment,
  CustomerEditor_Fetch,
  CustomerEditor_LoadHospitals,
  CustomerEditor_LoadRegions,
  CustomerEditor_Update,
  CustomerRef,
  CustomerSearch_Count,
  CustomerSearch_Search,
  CustomerType,
  GetMunicipalities,
  GetUserSelectUsers,
  OrganizationType,
  Workshift
} from "@graphql";
import {
  aoeSettingsInitialValues,
  customValidate,
  defaultSpans,
  FormData,
  formToGql,
  fragmentToForm,
  initialValues,
  schema
} from "./utils";
import { CustomerProfileLocation } from "../../navigation/locations/CustomerProfileLocation";

export class CustomerEditorStore extends FormStore<FormData> {
  @observable isLoadingRegions = false;
  @observable isLoadingHospitals = false;
  @observable municipalities: Option[] = [];
  @observable enabledUsers: Option[] = [];

  @observable private _allRegionCustomers: CustomerRef[] = [];
  @observable
  private _allHospitals: CustomerEditor_LoadHospitals.Return["customers"]["items"] = [];
  @observable private _customerData: CustomerEditor_CustomerFragment;

  constructor(
    public readonly navigation: Navigation,
    public readonly id?: string,
    initialData?: Partial<FormData>
  ) {
    super({
      schema,
      debounceValidationMs: 250,
      validate: () => customValidate(this.values)
    });

    this.registerReactions();

    const _initialValues = merge({}, initialValues, initialData);

    if (id) {
      this.fetch()
        .then((values) => {
          this.reset(merge({}, _initialValues, values));
        })
        .catch((e) => {
          store().snackbars.error(`Cannot fetch customer ${id}\nReason: ${e}`);
        })
        .finally(() => {
          this.loading = false;
        });
    } else {
      this.reset(_initialValues);
      this.loading = false;
    }

    (window as any).customerEditorStore = this;
  }

  @computed get isNew() {
    return !this.id;
  }

  @computed get canEditCustomerNumber() {
    return store().user.hasPermission("CanUpdateCustomerNumber");
  }

  get canEditGroupInvoicing() {
    return store().user.can("customer:update-group-invoicing:all");
  }

  @computed get allRegionCustomers(): Option[] {
    return this._allRegionCustomers
      .slice()
      .map((r) => ({ label: r.Name, value: r.Id }))
      .sortBy((x) => x.label);
  }

  @computed get allHospitals(): Option[] {
    return this._allHospitals
      .slice()
      .map((x) => x.Ref)
      .map((r) => ({ label: r.Name, value: r.Id }));
  }

  get allCountries() {
    return store()
      .cache.allCountriesWorldwide.filter((c) => c.canWorkIn)
      .map((c) => c.name);
  }

  get allCustomerGroups() {
    return store().cache.customerGroups;
  }

  get journalSystems() {
    return store().cache.systems;
  }

  @computed get allRegions() {
    if (!this.values.Address || !this.values.Address.Country) {
      return [];
    }

    return store().cache.getCountryRegions(this.values.Address.Country);
  }

  @computed get showMunicipalityInput() {
    if (
      this.values.Address?.Country === "Finland" ||
      this.values.Address?.Country === "Denmark"
    ) {
      return false;
    }

    return (
      (this.values.OrganizationType === OrganizationType.Commune &&
        this.values.Type === CustomerType.Region) ||
      (this.values.OrganizationType === OrganizationType.County &&
        this.values.Type === CustomerType.Hospital)
    );
  }

  @computed get isRegionCustomerSelectorVisible() {
    return this.isNew && this.values.Type && this.values.Type === CustomerType.Hospital;
  }

  @computed get displayHelperText() {
    return !this.values.OrganizationType && this.values.Type !== CustomerType.Region
      ? "Denna inställningen måste göras på organisationsnivå"
      : "";
  }

  @computed get isOrganizationType() {
    return this.values.Type === CustomerType.Region;
  }

  @computed get isOrgTypeSelectorEnabled() {
    return this.values.Type && this.values.Type === CustomerType.Region;
  }

  @computed get isRecruitingPortalTypeSelectorEnabled() {
    return this.values.Type && this.values.Type === CustomerType.Region;
  }

  @computed get isHospitalSelectorVisible() {
    return this.values.Type && this.values.Type === CustomerType.Department;
  }

  @computed get isProfessionsTabDisabled() {
    return this.isNew && this.values.Type !== CustomerType.Department;
  }

  @computed get isWorkshiftsTabDisabled() {
    return this.values.Type !== CustomerType.Department;
  }

  @computed get isProfessionsReadonly() {
    return this.isAOEInherited;
  }

  @computed get currentCustomerType() {
    return this._customerData?.Type;
  }

  @computed get isAOEInherited() {
    if (!this._customerData) {
      return false;
    }

    if (this._customerData.Type === CustomerType.Department) {
      return false;
    }

    if (
      this._customerData.Type === CustomerType.Hospital &&
      this._customerData.DirectChildren.length === 0
    ) {
      return false;
    }

    return true;
  }

  @computed get isOrderId() {
    if (!this._customerData) {
      return false;
    }

    return (
      this._customerData.Type === CustomerType.Department ||
      this._customerData.Type === CustomerType.Hospital
    );
  }

  @computed get allProfessions() {
    if (this._customerData && this.isAOEInherited) {
      return store().cache.professions.map((x) => {
        const disabled = this.isProfessionInherited(x);

        return {
          label: professions.getProfessionDisplayName(x),
          value: x,
          disabled,
          description: disabled ? locale.Disabled : ""
        };
      });
    }

    return store().cache.professions.toLabelValue(professions.getProfessionDisplayName);
  }

  @computed get showOverwriteWarning() {
    if (this.values.Type !== CustomerType.Department) {
      return false;
    }

    return this.hospital ? this.hospital.ShowHospitalAOERewriteWarning : false;
  }

  @computed get hospital() {
    if (!this.values.Hospital || this._allHospitals?.length === 0) {
      return null;
    }

    return this._allHospitals?.find((x) => x._id === this.values.Hospital.value);
  }

  @computed get isCustomerGroupsVisible() {
    return this.values.Type === CustomerType.Hospital;
  }

  isAOEDisabled(areaOfExpertise: string) {
    if (!this._customerData) {
      return false;
    }

    const aoe = this._customerData.AOESettings.find(
      (x) => x.AreaOfExpertise === areaOfExpertise
    );

    return aoe ? aoe.Inherited : false;
  }

  isProfessionInherited(prof: string) {
    if (!this._customerData) {
      return false;
    }

    const inheritedProfessions = this._customerData.AOESettings.filter(
      (x) => x.Inherited
    ).map((x) => x.Profession);

    return inheritedProfessions.includes(prof);
  }

  /** @override */
  setValue(name: string, value: any) {
    super.setValue(name, value);

    if (
      name === "OrganizationNumber" &&
      regexps.noOrgNum.test(value) &&
      !this.values.Address.Country
    ) {
      this.setValue("Address.Country", "Norway");
    }

    if (name === "Address.Country") {
      this.setValue("Address.Region", "");
    }
  }

  fetch = async () => {
    return CustomerEditor_Fetch.func(
      { id: this.id },
      { fetchPolicy: "network-only" }
    ).then(({ data }) => {
      this._customerData = data.customerById;
      return fragmentToForm(data.customerById);
    });
  };

  onSubmit = async () => {
    const values = toJS(this.values);
    const input = formToGql(values);
    const mutation = this.id
      ? CustomerEditor_Update.func(
          { id: this.id, input },
          {
            refetchQueries: [
              CustomerSearch_Search.OperationName,
              CustomerSearch_Count.OperationName
            ]
          }
        )
      : CustomerEditor_Create.func(
          { input },
          {
            refetchQueries: [
              CustomerSearch_Search.OperationName,
              CustomerSearch_Count.OperationName
            ]
          }
        );

    this.setSubmitting(true);

    return mutation
      .then(({ data: { submit } }) => {
        if (submit.validationErrors.length > 0) {
          this.setErrors(submit.validationErrors);
          return;
        }

        this.reset(values); // set form non-dirty
        this.navigation.navigate(new CustomerProfileLocation(submit.customer._id));
      })
      .catch((e) => {
        store().snackbars.error(`Cannot save customer ${input.Name}\nReason: ${e}`);
      })
      .finally(() => {
        this.setSubmitting(false);
      });
  };

  @action.bound selectWorkshiftSpan(
    profession: string,
    workshift: Workshift,
    value: boolean
  ) {
    const { WorkshiftSpans } = this.values;

    if (!workshift || !profession) {
      console.warn("something went wrong");
      return;
    }

    if (!value) {
      WorkshiftSpans.removeElement(
        (x) => x.Profession === profession && x.Workshift === workshift
      );
    } else {
      const existingSpan = WorkshiftSpans.find(
        (x) => x.Profession === profession && x.Workshift === workshift
      );

      // prevent duplicates
      if (existingSpan) {
        return;
      }

      WorkshiftSpans.push({
        Profession: profession,
        Workshift: workshift,
        ...defaultSpans[workshift]
      });
    }
  }

  @action.bound setWorkshiftSpanValue(
    key: "FromMinutes" | "ToMinutes",
    profession: string,
    workshift: Workshift,
    value: number | null
  ) {
    const { WorkshiftSpans } = this.values;

    const existingSpanIndex = WorkshiftSpans.findIndex(
      (x) => x.Profession === profession && x.Workshift === workshift
    );

    if (existingSpanIndex < 0) {
      // another option here would be to createFromGql a span instead of returning
      // but because the input control is disabled anyway, when the span doesn't exist
      // it doesn't matter. Otherwise creating a span would save the user a click on the checkbox
      return;
    }

    WorkshiftSpans[existingSpanIndex][key] = value;
  }

  getAOEsOnProfession(profession: string) {
    return store()
      .cache.getAreaOfExpOnProfessions([profession])
      .map((x) => x.Name);
  }

  getAOESettings(profession: string, areaOfExpertise: string) {
    const index = this.values.AOESettings.findIndex(
      (x) => x.Profession === profession && x.AreaOfExpertise === areaOfExpertise
    );

    if (index < 0) {
      return aoeSettingsInitialValues(profession, areaOfExpertise);
    }

    return this.values.AOESettings[index];
  }

  @action.bound setAOESettingsField<K extends keyof CustomerAOESettingsInput>(
    profession: string,
    areaOfExpertise: string,
    key: K,
    value: CustomerAOESettingsInput[K]
  ) {
    let index = this.values.AOESettings.findIndex(
      (x) => x.Profession === profession && x.AreaOfExpertise === areaOfExpertise
    );

    if (index < 0) {
      this.values.AOESettings.push(aoeSettingsInitialValues(profession, areaOfExpertise));
      index = this.values.AOESettings.length - 1;
    }

    this.values.AOESettings[index][key] = value;
    this.validate();
  }

  @action.bound setRegion(item: Partial<CustomerCardFragment> | null) {
    if (!item) {
      this.values.Region = null;
      this.validate();
      return;
    }

    if (item.Type !== CustomerType.Region) {
      console.error("Cannot set hospital, arguments are wrong:", item);
      return;
    }

    if (item.Address) {
      this.values.Address.Country = item.Address.Country;
      this.values.Address.Region = item.Address.Region;
      this.values.Address.City = item.Address.City;
    }

    this.values.Region = {
      label: item.Name,
      value: item._id
    };

    this.values.Hospital = null;

    this.validate();
  }

  @action.bound setHospital(item: Partial<CustomerCardFragment> | null) {
    if (!item) {
      this.values.Hospital = null;
      this.validate();
      return;
    }
    if (item.Type !== CustomerType.Hospital || item.Parents.length !== 1) {
      console.error("Cannot set hospital, arguments are wrong:", item);
      return;
    }

    if (item.Address) {
      this.values.Address.Country = item.Address.Country;
      this.values.Address.Region = item.Address.Region;
      this.values.Address.City = item.Address.City;
    }

    this.values.Region = {
      label: item.Parents[0].Name,
      value: item.Parents[0].Id
    };

    this.values.Hospital = {
      label: item.Name,
      value: item._id
    };

    this.validate();
  }

  private registerReactions() {
    autorun(() => {
      this._loadUsers();
    });

    reaction(
      () => this.showMunicipalityInput,
      (value) => this._loadMunicipalities(value),
      {
        fireImmediately: true
      }
    );

    reaction(
      () => this.values.Type,
      (value) => this._handleTypeChange(value),
      {
        fireImmediately: true
      }
    );

    reaction(
      () => this.values.Region,
      () => this._handleRegionIdChange()
    );

    reaction(
      () => Boolean(this.values.Address && !this.values.Address.Country),
      () => {
        if (!this.values.Address.Region) {
          return;
        }

        if (!this.allRegions.find((x) => x.name === this.values.Address.Region)) {
          this.values.Address.Region = "";
        }
      }
    );
  }

  private _loadMunicipalities(value: boolean) {
    if (value) {
      GetMunicipalities.func({})
        .then((res) => {
          this.municipalities = res.data.municipalities.map((x) => ({
            label: x.name,
            value: x._id
          }));
        })
        .catch((e) => {
          store().snackbars.error(`Cannot load municipalities\nReason: ${e}`);
        });
    } else {
      this.values.municipalityId = null;
    }
  }

  private _loadUsers() {
    GetUserSelectUsers.func({})
      .then((res) => {
        this.enabledUsers = res.data.users
          .filter((x) => x.Enabled)
          .map((x) => ({
            label: x.Name,
            value: x._id
          }));
      })
      .catch((e) => {
        store().snackbars.error(`Cannot load users\nReason: ${e}`);
      });
  }

  private _handleTypeChange(type: CustomerType) {
    // will load regions only once
    if (type && type !== CustomerType.Region && this._allRegionCustomers.length === 0) {
      this._loadRegions();
    }

    if (type === CustomerType.Region) {
      this.values.Region = null;
      this.values.Hospital = null;
    }

    if (type === CustomerType.Hospital) {
      this.values.Hospital = null;
    }
  }

  private _handleRegionIdChange() {
    this._loadHospitals();
  }

  private _loadRegions() {
    this.isLoadingRegions = true;

    CustomerEditor_LoadRegions.func({})
      .then(({ data }) => {
        this._allRegionCustomers = data.customers?.items?.map((c) => c.Ref);
      })
      .catch((e) => {
        store().snackbars.error(`Cannot load region customers\nReason: ${e}`);
      })
      .finally(() => {
        this.isLoadingRegions = false;
      });
  }

  private _loadHospitals() {
    if (!this.values.Region) {
      return;
    }

    this.isLoadingHospitals = true;

    CustomerEditor_LoadHospitals.func({ regionId: this.values.Region.value })
      .then(({ data }) => {
        this._allHospitals = data.customers.items;
      })
      .catch((e) => {
        store().snackbars.error(`Cannot load hospitals\nReason: ${e}`);
      })
      .finally(() => {
        this.isLoadingHospitals = false;
      });
  }
}
